import React, { useLayoutEffect, useRef } from 'react';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { isLevelSenseDevice, isPeopleSenseDevice } from '../../../common/helper';

type ChartType = 'line' | 'column';

interface IChartSetting {
  name: string;
  field: string;
  unit: string;
  color: string;
  chartType: ChartType;
}

interface IChartSettings {
  level: IChartSetting;
  ultrasonicDistance: IChartSetting;
  temperature: IChartSetting;
  totalCount: IChartSetting;
  count: IChartSetting;
}

const chartSettings: IChartSettings = {
  level: { name: 'Level', field: 'level', unit: '%', color: '#9bd400', chartType: 'line' },
  ultrasonicDistance: { name: 'Ultrasonic Distance', field: 'ultrasonicDistance', unit: 'cm', color: '#ffb900', chartType: 'line' },
  totalCount: { name: 'Total Count', field: 'totalCount', unit: '', color: '#9bd400', chartType: 'line' },
  count: { name: 'Count', field: 'count', unit: '', color: '#00b0f0', chartType: 'column' },
  temperature: { name: 'Temperature', field: 'temperature', unit: '°C', color: '#cccccc', chartType: 'line' },
}

interface IChartData {
  time: number;
  level?: number;
  ultrasonicDistance?: number;
  totalCount?: number;
  count?: number;
  temperature: number;
};

interface IEventAmChartsProps {
  events: any[];
  device: any;
}

const EventAmCharts: React.FC<IEventAmChartsProps> = ({ events, device }) => {
  const divRef = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    // Function to create X axis
    const createXAxis = (
      root: am5.Root,
      chart: am5xy.XYChart,
    ) => {
      root._logo?.dispose();
      return chart.xAxes.push(
        // Use DateAxis here instead of CategoryAxis. CategoryAxis is intended for discrete, labeled categories, not time-series data
        am5xy.DateAxis.new(root, {
          maxDeviation: 0.1,
          baseInterval: {
            timeUnit: "minute",
            count: 1,
          },
          renderer: am5xy.AxisRendererX.new(root, {
            minGridDistance: 80,
            minorGridEnabled: true,
          }),
          tooltip: am5.Tooltip.new(root, {}),
        })
      );
    };

    // Function to create Y axis and series
    const createYAxis = (
      root: am5.Root,
      chart: am5xy.XYChart,
      chartSetting: IChartSetting,
      xAxis:  am5xy.DateAxis<am5xy.AxisRenderer>,
      data: IChartData[],
      opposite: boolean,
    ) => {
      const yAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          renderer: am5xy.AxisRendererY.new(root, {
            opposite,
          }),
        })
      );
      yAxis.children.push(
        am5.Label.new(root, {
          text: chartSetting.unit ? `${chartSetting.name} (${chartSetting.unit})` : chartSetting.name,
          rotation: -90,
          y: am5.p50,
          centerY: am5.p50,
          centerX: am5.p50,
          fontWeight: "600",
          fill: am5.color(chartSetting.color),
        })
      );
      const renderer = yAxis.get('renderer');
      renderer.grid.template.setAll({
        stroke: am5.color(chartSetting.color),
        strokeOpacity: 0.2,
      });
      renderer.labels.template.setAll({
        fill: am5.color(chartSetting.color),
      });

      const settings = {
        name: chartSetting.name,
        xAxis,
        yAxis: yAxis,
        valueXField: 'time',
        valueYField: chartSetting.field,
        stroke: am5.color(chartSetting.color),
        tooltip: am5.Tooltip.new(root, {
          labelText: chartSetting.name + (chartSetting.unit ? ` : {valueY}${chartSetting.unit}` : ` : {valueY}`),
          background: am5.RoundedRectangle.new(root, { fillOpacity: 0.8 })
        }),
      };

      const chartType = chartSetting.chartType;
      if (chartType === 'line') { // Line chart
        const series = chart.series.push(
          am5xy.LineSeries.new(root, settings)
        );
        series.bullets.push(function () {
          let bulletCircle = am5.Circle.new(root, {
            radius: 0,
            fill: series.get("fill")
          });
          return am5.Bullet.new(root, {
            sprite: bulletCircle
          });
        });

        series.data.setAll(data);

        return series;
      } else if (chartType === 'column') { // Column chart
        const series = chart.series.push(
          am5xy.ColumnSeries.new(root, settings)
        );
        series.bullets.push(function () {
          let bulletCircle = am5.Circle.new(root, {
            radius: 0,
            fill: series.get("fill")
          });
          return am5.Bullet.new(root, {
            sprite: bulletCircle
          });
        });

        series.data.setAll(data);

        return series;
      }

      throw new Error(`Unsupported chart type: ${chartType}`);
    };

    // Function to render chart
    const renderChart = (events: any[]) => {
      if (!divRef.current) return;

      // Create root element
      const root = am5.Root.new(divRef.current);

      // Function to configure a series tooltip to display values from multiple series,
      // resolving the AMCharts issue where only one tooltip appears when using snapToSeries
      // with multiple series
      const setTooltip = (series: am5xy.XYSeries, chartSettings: IChartSetting[]) => {
        const tooltipText = chartSettings.map(chartSetting => {
          const value = chartSetting.unit ? ` : {${chartSetting.field}}${chartSetting.unit}` : ` : {${chartSetting.field}}`;
          return chartSetting.name + value;
        }).join('\n');

        (series as am5xy.XYSeries).set("tooltip", am5.Tooltip.new(root, {
          pointerOrientation: "horizontal",
          labelText: tooltipText,
        }));
      }

      // Set themes
      // https://www.amcharts.com/docs/v5/concepts/themes/
      const theme = am5themes_Animated.new(root);
      root.setThemes([theme]);

      // Create chart
      const chart = root.container.children.push(
        am5xy.XYChart.new(root, {
          panX: false,
          panY: false,
          wheelX: 'panX',
          wheelY: 'zoomX',
        })
      );

      // Create X Axis and left and right Y Axis
      const xAxis = createXAxis(root, chart);
      const allSeries: (am5xy.ColumnSeries | am5xy.LineSeries)[] = [];
      
      // LevelSense
      if (isLevelSenseDevice(device)) {
        // Be careful with the order of events. We can reverse events then use events.map but this
        // will not be efficient if there are a lot of events. Instead we can loop through events
        // in reverse order and push to data, which is more efficient.
        const data = [];
        for (let i = events.length - 1; i >= 0; i--) {
          const event = events[i];
          data.push({
            time: new Date(event.eventTime).getTime(),
            level: event.level,
            ultrasonicDistance: event.level === undefined ? event.ultrasonicDistance : undefined,
            temperature: event.temperature,
          });
        }

        // Add temperature - need to base tooltip on this as it is the only constant series
        allSeries.push(createYAxis(root, chart, chartSettings.temperature, xAxis, data, true));
        
        // Level always present (check US never present as it is filtered out in data collection above)
        if (data.every((item: any) => item.ultrasonicDistance === undefined)) {
          allSeries.push(createYAxis(root, chart, chartSettings.level, xAxis, data, false));
          setTooltip(allSeries[0], [chartSettings.level, chartSettings.temperature]);
        }
        // Level never present
        else if (data.every((item: any) => item.level === undefined)) {
          allSeries.push(createYAxis(root, chart, chartSettings.ultrasonicDistance, xAxis, data, false));
          setTooltip(allSeries[0], [chartSettings.ultrasonicDistance, chartSettings.temperature]);
        }
        // Combination of level and ultrasonic distance
        else {
          allSeries.push(
            createYAxis(root, chart, chartSettings.level, xAxis, data, false),
            createYAxis(root, chart, chartSettings.ultrasonicDistance, xAxis, data, false)
          );
          setTooltip(allSeries[0], [chartSettings.level, chartSettings.ultrasonicDistance, chartSettings.temperature]);
        }
      }
      // PeopleSense
      else if (isPeopleSenseDevice(device)) {
        // Be careful with the order of events
        const data = [];
        for (let i = events.length - 1; i >= 0; i--) {
          const event = events[i];
          data.push({
            time: new Date(event.eventTime).getTime(),
            totalCount: event.totalCount,
            count: event.count,
            temperature: event.temperature,
          });
        }

        // Set count period in axis name
        const countIntervalMins = events[0]?.countIntervalMins ?? 0;
        if (countIntervalMins > 0) chartSettings.count.name = `Count (${countIntervalMins} mins)`;

        // Add count series
        allSeries.push(
          createYAxis(root, chart, chartSettings.totalCount, xAxis, data, false),
          createYAxis(root, chart, chartSettings.count, xAxis, data, true),
        );

        // Only PeopleSense-03 has temperature
        if (device?.DeviceTypeName === 'PeopleSense-03') {
          allSeries.push(createYAxis(root, chart, chartSettings.temperature, xAxis, data, true));
          setTooltip(allSeries[0], [chartSettings.totalCount, chartSettings.count, chartSettings.temperature]);
        } else {
          setTooltip(allSeries[0], [chartSettings.totalCount, chartSettings.count]);
        }
      }
      // Otherwise throw error since we should only render this chart for LevelSense and PeopleSense devices
      else {
        throw new Error(`Unsupported device type: ${device.DeviceTypeName}`);
      }

      // Cursor
      let cursor = chart.set("cursor", am5xy.XYCursor.new(root, {
        behavior: "zoomX",
        snapToSeries: allSeries,
        snapToSeriesBy: "x",
      }));
      cursor.lineY.set("visible", false);

      // Scrollbar
      // https://www.amcharts.com/docs/v5/charts/xy-chart/scrollbars/
      chart.set("scrollbarX", am5.Scrollbar.new(root, {
        orientation: "horizontal",
      }));

      // Add legend
      // https://www.amcharts.com/docs/v5/charts/xy-chart/legend-xy-series/
      var legend = chart.bottomAxesContainer.children.push(
        am5.Legend.new(root, {
          centerX: am5.p50,
          x: am5.p50,
        })
      );
      allSeries.forEach(series => legend.data.push(series));

      // Animation
      chart.appear(1000, 100);

      return root;
    }

    const root = renderChart(events);

    // Cleanup
    return () => {
      root?.dispose();
    };
  }, [events, device]);

  return <div ref={divRef} style={{ width: '100%', height: 360 }} />;
};

export default EventAmCharts;
